/**
 * Fatiado de ReviewPanel.tsx (#299, R3): o cartão «Idioma do Menu» da
 * revisão — par Menu × restaurante (crit. 9), confirmação do idioma
 * (#301/#304) e o aviso bloqueante da MOEDA (decisão B, versão I2).
 *
 * DECISÃO B (moeda): o import só aceita a MOEDA DO RESTAURANTE. Não há
 * seletor de moedas nem conversão de preços. O aviso bloqueante tem UMA
 * ação: «Os preços estão em {currency}» (confirma a correção da leitura;
 * só conta depois de o servidor responder OK). A saída «mudar o país e a
 * moeda do restaurante» é SÓ TEXTO (fale com a equipe da Pratzu).
 * Moeda deduzida do idioma (currency_detected falso) tem texto próprio e
 * nunca mostra «—». Depois de (a): «Moeda confirmada: {currency} ✓» na
 * região role="status" já montada (o foco vai para «Importar», no painel).
 */
import { useI18n } from '../../lib/i18n.js'
import { Button } from '../../components/ui/index.js'
import type { LocaleOption } from '../../lib/import-locale.js'
import { currencyName } from '../../lib/currency-names.js'

export type ReviewLocaleCurrencyProps = {
  locale: string
  /** idioma principal do restaurante, em base (#428) ou '' */
  restaurantLocale: string
  localeOptions: LocaleOption[]
  localeNeedsConfirm: boolean
  onPickLocale: (code: string) => void
  /** moeda em exibição (lida no Menu, corrigida ou padrão do restaurante) */
  currency: string
  /** false = moeda foi DEDUZIDA do idioma, não foi lida no cardápio */
  currencyDetected: boolean
  /** true = moeda do Menu ≠ a do restaurante e falta a correção da leitura */
  currencyNeedsConfirm: boolean
  /** moeda lida no Menu ou deduzida do idioma — nunca «—» (calculada no painel) */
  currencySuggested: string | null
  /** moeda do restaurante — a única que o import aceita (decisão B) */
  restaurantCurrency: string | null
  /** confirma a correção da leitura (POST /:id/currency); devolve a promessa */
  onPickCurrency: (code: string) => Promise<void> | void
  /** POST /currency em curso: aria-busy + botão preso */
  currencyPending?: boolean
  /** correção já gravada no SERVIDOR (view.currency_confirmed) */
  currencyConfirmed?: boolean
  /** 422 CURRENCY_CONFIRMATION_REQUIRED do commit: reabre a faixa (role="alert") */
  mismatchAlert?: boolean
  /**
   * #486 (item 2): erro do import com chave i18n (POST /currency, 422 de
   * idioma do commit) — INLINE no cartão com role="alert" e foco, para o
   * leitor de voz saber onde resolver em vez de adivinhar.
   */
  error?: string | null
}

export function ReviewLocaleCurrency(props: ReviewLocaleCurrencyProps) {
  const { t, lang } = useI18n()
  const {
    locale,
    restaurantLocale,
    localeOptions,
    localeNeedsConfirm,
    onPickLocale,
    currency,
    currencyDetected,
    currencyNeedsConfirm,
    currencySuggested,
    restaurantCurrency,
    onPickCurrency,
    currencyPending,
    currencyConfirmed,
    mismatchAlert,
    error,
  } = props
  const localeLabel = (code: string) => localeOptions.find((o) => o.code === code)?.label ?? code
  // idiomas diferentes → os pratos entram no do Menu; traduções vêm depois
  const localesDiffer =
    !!locale.trim() &&
    !!restaurantLocale &&
    locale.split(/[-_]/)[0] !== restaurantLocale.split(/[-_]/)[0]
  const dirOf = (code: string) => (code === 'ar' ? 'rtl' : 'ltr')
  // nomes «rial saudita (SAR)» para o aviso (Intl na língua do painel)
  const named = (code: string | null) => (code ? currencyName(code, lang) : '—')
  // restaurante sem país/moeda: região visível com currency_restaurant_missing
  const showRegion =
    currencyNeedsConfirm ||
    currencyConfirmed === true ||
    mismatchAlert === true ||
    !restaurantCurrency
  const confirmedShown = currencyConfirmed === true && !currencyNeedsConfirm
  // #486 (item 2): sem país/moeda é ERRO bloqueante (currency_restaurant_missing)
  // e o 422 reabre o aviso — anúncio ASSERTIVO (role=alert); o resto da região
  // segue polite (status). Sem aria-live explícito no alert: o role já implica
  // assertive e um aria-live="polite" sobrepô-lo-ia.
  const regionAlert = mismatchAlert === true || !restaurantCurrency

  return (
    <div
      id="import-currency-card"
      tabIndex={-1}
      className="rounded-2xl border border-gray-200 bg-white p-4 space-y-2"
      aria-busy={currencyPending || undefined}
    >
      {/* «Idioma do Menu» é título E rótulo do seletor (htmlFor, revisão UX) */}
      <label htmlFor="import-locale-select" className="block text-sm font-bold text-gray-900">
        {t('admin.import.language')}
        {localeNeedsConfirm && (
          <span
            data-testid="import-locale-confirm-badge"
            className="ml-2 rounded-full bg-amber-100 text-amber-800 text-xs px-2 py-0.5"
          >
            {t('admin.import.confirm_language')}
          </span>
        )}
      </label>
      {/* crit. 9: linha própria distingue o idioma do Menu do do restaurante;
          <bdi lang dir> isola cada nome (o painel pode estar em árabe) */}
      {restaurantLocale && (
        <p className="text-xs text-gray-500" data-testid="import-restaurant-locale">
          {t('admin.import.menu_locale_label')}:{' '}
          <bdi lang={locale || undefined} dir={dirOf(locale)}>
            {localeLabel(locale)}
          </bdi>
          {' · '}
          {t('admin.import.restaurant_locale_label')}:{' '}
          <bdi lang={restaurantLocale || undefined} dir={dirOf(restaurantLocale)}>
            {localeLabel(restaurantLocale)}
          </bdi>
        </p>
      )}
      {localesDiffer && (
        <p className="text-xs text-gray-500" data-testid="import-locale-note">
          {t('admin.import.locale_translations_note', {
            menu: localeLabel(locale),
            restaurant: localeLabel(restaurantLocale),
          })}
        </p>
      )}
      {localeNeedsConfirm && (
        <div className="flex flex-wrap gap-2">
          {[...new Set([locale, restaurantLocale].filter((l): l is string => !!l))].map((code) => (
            <button
              key={code}
              type="button"
              data-testid={`import-locale-pick-${code}`}
              lang={code}
              dir={dirOf(code)}
              onClick={() => onPickLocale(code)}
              className="rounded-full border border-amber-300 bg-white text-gray-700 text-xs px-3 min-h-11 inline-flex items-center hover:bg-amber-100"
            >
              {localeLabel(code)}
            </button>
          ))}
        </div>
      )}
      <select
        id="import-locale-select"
        data-testid="import-locale-select"
        value={locale}
        onChange={(e) => onPickLocale(e.target.value)}
        className="rounded-lg border border-gray-200 px-3 py-2 text-sm w-full max-w-xs min-h-11"
      >
        {!locale && <option value="">—</option>}
        {localeOptions.map((o) => (
          <option key={o.code} value={o.code} lang={o.code} dir={dirOf(o.code)}>
            {o.label}
          </option>
        ))}
      </select>
      {/* moeda: só leitura — item 8 (UX): lida × restaurante, sem «—» */}
      <p
        className="text-xs text-gray-500"
        data-testid="import-currency"
        data-detected={currencyDetected === false ? 'false' : 'true'}
      >
        <span aria-hidden="true">💱</span>{' '}
        {/* M1/7: depois de confirmar, a moeda do restaurante é que fica */}
        {currencyConfirmed === true
          ? t('admin.import.currency_line', {
              detected: restaurantCurrency ?? '—',
              restaurant: restaurantCurrency ?? '—',
            })
          : currencyDetected
            ? t('admin.import.currency_line', {
                detected: currencySuggested ?? (currency || '—'),
                restaurant: restaurantCurrency ?? '—',
              })
            : t('admin.import.currency_line_deduced', { restaurant: restaurantCurrency ?? '—' })}
      </p>
      {/* #486 (item 2): erro do import com chave i18n INLINE (role="alert") —
          anunciado e lido junto da ação que falhou (POST /currency, 422 de
          idioma do commit); o foco vem para o cartão */}
      {error && (
        <p
          role="alert"
          data-testid="import-currency-error"
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
        >
          {error}
        </p>
      )}
      {/* região SEMPRE montada enquanto houver moeda a resolver (ou já
          resolvida): o anúncio role="status" não nasce junto com a mudança */}
      {showRegion && (
        <div
          role={regionAlert ? 'alert' : 'status'}
          aria-live={regionAlert ? undefined : 'polite'}
          className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 space-y-2"
        >
          {confirmedShown ? (
            <p className="text-sm text-amber-900" data-testid="import-currency-confirmed">
              {t('admin.import.currency_confirmed_line', { currency })}
            </p>
          ) : restaurantCurrency ? (
            <>
              <p className="text-sm text-amber-900" data-testid="import-currency-mismatch">
                {currencyDetected
                  ? t('admin.import.currency_mismatch', {
                      detected: named(currencySuggested),
                      restaurant: named(restaurantCurrency),
                    })
                  : t('admin.import.currency_mismatch_deduced', {
                      menu: localeLabel(locale),
                      detected: named(currencySuggested),
                      restaurant: named(restaurantCurrency),
                    })}
              </p>
              {/* (a) primária, ação ÚNICA (spec-arq §4): um facto que o dono
                  confirma — nunca «Confirmar EUR» (lê-se como conversão). A
                  saída (b) é SÓ o texto acima («fale com a equipe da Pratzu»):
                  a importação não se cancela — fica guardada e retoma-se em
                  ?id= (spec-arq §4) */}
              <Button
                variant="primary"
                type="button"
                id="import-currency-confirm-restaurant"
                data-testid="import-currency-confirm-restaurant"
                disabled={currencyPending}
                aria-busy={currencyPending || undefined}
                onClick={() => onPickCurrency(restaurantCurrency)}
                className="w-full min-h-11"
              >
                {t('admin.import.currency_confirm_restaurant', {
                  currency: restaurantCurrency,
                })}
              </Button>
            </>
          ) : (
            /* spec-arq §3: restaurante sem país/moeda — só o texto; a saída
               (a) não existe (não há moeda a confirmar) */
            <p className="text-sm text-amber-900" data-testid="import-currency-missing">
              {t('admin.import.currency_restaurant_missing')}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
